/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { I18nService, PermissionService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { healthStatusE, LifProtoCol, MO } from 'app/common/constants';
import { DiskTypeE, DiskTypeOpts } from 'app/common/constants/disk-type';
import { LicenseKey } from 'app/common/constants/license-key';
import { ToolService } from 'app/common/services';
import { ProductConfig, SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';

import { CurSupplyModeTypeE } from './device/detail/detail.const';
import { CarrierModelE } from './device/device.const';
import { intfModuleList } from './device/intfModuleConfig';
import { SwitchPortStatusE } from './summary/switch/switch-port/switch-port.const';

/**
 * 端口类型
 */
export enum PortType {
    ETH = '1',
    BOND = '7',
    VLAN = '8',
    VIP = '25',
    SIP = '26'
}

/**
 * 控制框间组网类型
 */
export enum ScaleOutTypeE {
    /**
     * 直连组网
     */
    DIRECT_CONNECTION = 0,
    /**
     * 交换机组网
     */
    SWITCH_NETWORKING = 1,
    /**
     * 单引擎，无组网
     */
    SINGLE_ENGINE = 2
}

/**
 * 资产清单卡片key
 */
export enum InventoryCardKeyE {
    /**
     * 机框
     */
    ENCLOSURE = 'enclosure',
    /**
     * 控制器
     */
    CONTROLLER = 'controller',
    /**
     * 硬盘
     */
    DISK = 'disk',
    /**
     * 端口
     */
    PORT = 'port',
    /**
     * 管理板
     */
    MANAGEMENTBOARD = 'managementBoard',
    /**
     * 计算节点
     */
    COMPUTENODE = 'computeNode',
    /**
     * 交换机
     */
    SCALE_OUT_SWITCH = 'switch',
}

/**
 * 5910接口卡左右槽位图形
 */
export enum DB5910IntfModuleTypeE {
    SLOT0 = 'DB5910_left',
    SLOT1 = 'DB5910_right'
}

export enum RoleTypeE {
    MANAGEMENT = 1,
    SERVICE = 2,
    MANAGE_AND_SERVICE = 3,
    REPLICATION = 4,
    CLIENT = 8,
    VTEP = 9,
    HEALTH_CHECK = 10,
    DATA_BACKUP = 11,
    SYSTEM_MANAGEMENT = 12,
}

export enum SupportProtocalE {
    NFS = 1,
    CIFS = 2,
    NFS_AND_CIFS = 3,
    iSCSI = 4
}
export enum TrustE {
    PCP = '0',
    DSCP = '1'
}

/**
 * Bond端口-绑定端口类型参数取值。
 */
export enum BondPortTypeE {
    HOST = '1',
    SWITCH_PORT = '2',
    CLOUD_VXLAN = '4'
}

export enum VlanFatherDrvTypeE {
    ETH = '1',
    ROCE = '2'
}
export enum DataProtocolE {
    NFS = '1',
    CIFS = '2',
    iSCSI = '3',
    NFSCIFS = '4',
    NVMeoverRoCE = '64'
}
export enum LogicTypeE {
    SCALE_OUT = '11',
    SCALE_UP = '1',
    HOST = '0',
    CONTAINER_FRONT = '13',
    CONTAINER_BACKEND = '14',
    SWITCH_PORT = '16',
}
export enum physicalTypeE {
    ETH = '1',
    ROCE = '2'
}

/**
 * Vxlan功能开关
 */
export enum CloudVxlanSwitchE {
    OFF = '0',
    ON = '1'
}

/**
 * Vxlan功能开关关闭时屏蔽参数
 */
export enum CloudVxlanSwitchIgnoreE {
    bondPortType = 'bondPortType'
}
@Injectable({
    providedIn: 'root',
})
export class HardwareStrService {
    i18nLabel = {
        // create
        // 资产清单
        summaryTab: this.get('system.hardware_term_resourceList_tab'),
        enclosure: this.get('system.hardware_term_chassis_lab'),
        enclosures: this.get('system.hardware_term_chassis_lab'),
        controllers: this.get('common.controllers'),
        managementBoards: this.get('system.hardware_term_ManagementBoards_title'),
        computeNodes: this.get('system.hardware_term_computeNodes_title'),
        disks: this.get('common.Disks'),
        ports: this.get('common.Ports'),
        port: this.get('common.port'),
        ctrlEnclosures: this.get('common.controllerEnclousres_tab'),
        diskEnclosures: this.get('common.diskEnclousres_tab'),
        total: this.get('home.alarm_term_total_lab'),
        fault: this.get('common.EnumDefine_HEALTH_STATUS_fault'),
        healthy: this.get('common.EnumDefine_RUNNING_STATUS_E_HEALTHY'),
        lifPort: this.get('common.logicalPort_label'),
        lifPorts: this.get('common.LogicalPorts'),
        vlanPort: this.get('system.hardware_term_VLAN_tab'),
        vlanPorts: this.get('system.hardware_term_VLANs_tab'),
        failoverGroup: this.get('system.network_term_failoverGroups_tab'),
        failoverGroupLabel: this.get('system.logicPort_task_create_FailoverGroup_label'),
        wwnLabel: this.get('system.LIF_task_view_owning_col'),
        localPortLabel: this.get('system.LIF_task_view_Preferred_local_value'),
        remotePortLabel: this.get('system.LIF_task_view_Preferred_remote_value'),
        portNum: this.get('system.hardware_term_portsNum_column'),
        temperatureLabel: this.get('system.hardware_term_diskTemperature_label'),
        switchs: this.get('system.hardware_term_SOSwitches_tab'),
        switchPorts: this.get('system.hardware_term_SOSwitchPorts_tab'),
        // lif
        moreBtn: this.get('common.more_button'),
        actionBtn: this.get('common.operation_button'),
        failbackBtn: this.get('system.logicPort_task_switchback_button'),
        activateBtn: this.get('common.active_button'),
        deactiveBtn: this.get('common.inactive_button'),
        ipAddrFailover: this.get('system.logicPort_task_create_excursionStatus_label'),
        failbackMode: this.get('system.logicPort_task_create_switchbackStatus_label'),
        dynamicDNS: this.get('system.logicPort_task_create_DDNS_label'),
        vStoreName: this.get('common.vstoreName_lab'),
        vStoreLab: this.get('common.owningVstore_lab'),
        vStoreNameLab: this.get('vstore_name_lab'),
        vStoreIdLab: this.get('vstore_id_lab'),
        dataProtocol: this.get('system.logicPort_task_create_dataProtocol_label'),
        homePort: this.get('system.logicPort_task_view_primaryPort_label'),
        currentPort: this.get('system.logicPort_task_view_currentPort_label'),
        homeController: this.get('system.logicPort_task_view_primaryController_label'),
        currentController: this.get('system.logicPort_task_view_currentController_label'),
        statusLabel: this.get('common.status_label'),
        activeStatusLabel: this.get('protection.vstore_hyperMetroDomain_task_view_activeStatus_lab'),
        role: this.get('system.role_label'),
        dnsZone: this.get('system.logicPort_task_create_DNSZone_lab'),
        listenDNSQueryReq: this.get('system.logicPort_task_create_listenDNSRequest_label'),
        manageAccessMode: this.get('system.logicPort_task_view_manageAccessMethod_label'),
        enable: this.get('common.enable_label'),
        auto: this.get('common.EnumDefine_STARTUP_MODE_E_AUTO'),
        manu: this.get('common.EnumDefine_STARTUP_MODE_E_MANUAL'),
        activeNow: this.get('system.logicPort_task_instantActivate_label'),
        failbackLifTitle: this.get('system.logicPort_task_switchbackPort_tittle'),
        failbackLifName: (name: string): string => this.get('rest.failbackLogicPort_msg', [name]),
        homePortSearchPlaceholder: this.get('system.logicPort_task_view_searchPrimaryPort_value'),
        NFSLab: this.get('common.NFS_lab'),
        NFSOverRDMALab: this.get('settings.fileStorageService_term_nfsService_NFSoverRDMA_lab'),
        CIFSLab: this.get('common.CIFS_lab'),
        iSCSILab: this.get('common.iSCSI_lab'),
        NFSCIFSLab: this.get('common.NFSCIFS_lab'),
        DataTurboLab: this.get('common.DataTurbo_lab'),
        DataTurboOverRoceLab: this.get('common.DataTurbo_RoCE_lab'),
        IPv4Label: this.get('common.IPv4_label'),
        IPv6Label: this.get('common.IPv6_label'),
        RoleSelectionTips: this.get('system.systemSetting_network_task_createLIF_role_tip'),
        RoleSelectionXseriseTips: this.get('system.systemSetting_network_task_createLIF_role_Xserise_tip'),
        RoleSelectionDradoTips: this.get('system.systemSetting_network_task_createLIF_role_dorado_tip'),
        RoleSelectionDataBackupTip: this.get('system.systemSetting_network_task_createLIF_role_dataBackup_tip'),
        RoleSelectionClientTip: this.get('system.systemSetting_network_task_createLIF_role_client_tip'),
        RoleSelectionVxlanTip: this.get('system.systemSetting_network_task_createLIF_role_VTEP_tip'),
        RoleSelectionSystemManagementTip: this.get('system.systemSetting_network_task_createLIF_role_systemAdmin_tip'),
        roleSelectionReplicationTip: this.get('system.systemSetting_network_task_createLIF_role_replication_tip'),
        dataProtocolNasTips: (params: string): string =>
            this.get('system.logicPort_simplifiedMode_task_create_dataProtocol_NAS_tip', [params]),
        dataProtocolSanTips: (params: string): string =>
            this.get('system.logicPort_simplifiedMode_task_create_dataProtocol_SAN_tip', [params]),
        dataProtocolDataturboTips: (params: string): string =>
            this.get('system.logicPort_simplifiedMode_task_create_dataProtocol_dataturbo_tip', [params]),
        slightPauseMarkLab: this.get('common.slightPauseMark_lab'),
        lifUnSupportTip: this.get('service.logicalPort_term_notSupport_tip'),
        ownedControllerLab: this.get('service.fileSystem_task_view_ownedController_lab'),
        activatedLab: this.get('common.EnumDefine_RUNNING_STATUS_E_ACTIVATED'),
        /** 批量查询漂移组 */
        batchGetFailoverGroup: this.get('rest.batchQueryFailoverGroup_msg'),
        selectVstoreFirst: this.get('service.share_term_create_chooseVstore_tip'),

        // 硬件
        deviceTab: this.get('system.Devices_tab'),
        bay: this.get('system.Bay_label'),
        diskEnclosure: this.get('common.diskEnclousre_lab'),
        ctrlEnclosure: this.get('common.controllerEnclousre_lab'),
        dswEnclosure: this.get('system.EnumDefine_ENCLOSURE_TYPE_E_DSW'),
        controller: this.get('system.EnumDefine_MOTYPE_CONTROLLER'),
        expboard: this.get('system.hardware_term_expansionBoard_label'),
        power: this.get('system.EnumDefine_MOTYPE_POWER'),
        serialPort: this.get('system.EnumDefine_MOTYPE_SERIAL_PORT'),
        fcPort: this.get('system.EnumDefine_MOTYPE_FC_PORT'),
        fcPorts: this.get('system.network_term_FCPorts_tab'),
        disk: this.get('monitor.charts_term_objectType_DISK'),
        ethPort: this.get('common.EnumDefine_MOTYPE_ETH_PORT'),
        roceport: this.get('system.network_term_rocePort_value'),
        rocePort: this.get('system.device_term_RoCEPort_label'),
        ethPorts: this.get('system.network_term_etherPorts_tab'),
        diskDrawer: FeatureConfig.IsDbStoreIntegratedMachine ?
            this.get('system.hardware_term_diskDrawer_lab') :
            this.get('compute.hardware_term_diskDrawer_lab'),
        riser: this.get('system.hardware_term_RiserCard_label'),
        // 术语变更 riser-->carrier-->box
        carrier: this.get('system.hardware_term_riser_lab'),
        managementBoard: this.get('system.hardware_term_ManagementBoard_label'),
        computeNode: this.get('system.hardware_term_computeNode_label'),

        rocePorts: this.get('system.network_term_rocePorts_tab'),
        snsdBtn: this.get('system.network_roce_view_SNSD_column'),
        trust: this.get('system.network_roce_view_trustMode_col'),
        fan: this.get('common.fan_lab'),
        fanGroup: this.get('system.EnumDefine_MOTYPE_FAN'),
        powerGroup: this.get('system.EnumDefine_MOTYPE_POWER'),
        BBU: this.get('system.EnumDefine_MOTYPE_BACKUP_POWER'),
        sasPort: this.get('system.device_term_SASPort_left_title'),
        fanCtrlModule: this.get('system.device_controlEnsoure_term_coolingUnit_title'),
        logicalPortValueRangeLabel: this.get('common.valueRange_label', [1, 128]),
        overwritesValueRangeLabel: this.get('common.valueRange_label', [1, 15]),
        dataToBeVerifiedValueRangeLabel: this.get('common.valueRange_label', [1, 100]),
        name: this.get('common.name_column'),
        location: this.get('common.location_column'),
        healthStatus: this.get('common.healthStatus_column'),
        runningStatus: this.get('common.runningStatus_column'),
        workSpeed: this.get('system.hardware_term_runSpeed_label'),
        maxSpeed: this.get('system.hardware_term_maxRunSpeed_label'),
        portSwitch: this.get('system.device_term_portSwitch_label'),
        portState: this.get('system.device_term_portStatus_label'),
        initiatorNum: this.get('system.common_term_hostInitiatorNum_label'),
        protocolTypeLab: this.get('system.FCPort_task_modify_protocolType_lab'),
        immediateDataLab: this.i18n.get('protection.configuration_remoteDevice_task_addRemoteDevice_immediateData_lab'),
        WWPN: this.get('common.WWPN_lab'),
        WWN: this.get('common.device_WWN_label'),
        runningModel: this.get('system.secureVideo_task_create_para_runMode_label'),
        macAddress: this.get('system.hardware_term_Macaddress_label'),
        ipv4AndMask: this.get('common.term_IPV4address') + '/' + this.get('common.term_subNetworkMask'),
        ipv6AndPrefix: this.get('common.term_IPV6address') + '/' + this.get('common.preix_label'),
        maxUnit: this.get('system.device_controlEnsoure_view_Ethport_maxTransUnit'),
        bindName: this.get('system.hardware_term_bondingName'),
        logicType: this.get('system.device_controlEnsoure_view_Ethport_portLogicType_lab'),
        capacity: this.get('system.capacity_column'),
        manuCapacity: this.get('system.hardware_term_ManufaturCap_column'),
        diskDomain: this.get('monitor.charts_term_objectType_DISKPOOL'),
        storagepool: this.get('monitor.charts_term_objectType_STORAGEPOOL'),
        type: this.get('system.type_column'),
        diskRole: this.get('system.role_label'),
        abrasion: this.get('system.hardware_term_abrasion_label'),
        lifeSpan: this.get('system.hardware_term_LifeSpanForMonth_label'),
        bomCode: this.get('system.common_term_electricItem_label'),
        barcode: this.get('system.hardware_term_barcode_column'),
        encyptType: this.get('common.encryptionType_label'),
        AKoutTime: this.get('system.hardware_term_AKexpirationDate_lab'),
        diskPositionTip: this.get('common.hardware_task_Locate_error_tips'),
        diskUnPositionTip: this.get('common.hardware_task_cancelLocate_error_tips'),
        eraseDataTip: this.get('common.hardware_task_dataEraser_error_tips'),
        ethPortManageRouteTip: this.get('common.hardware_task_route_ethPort_error_tips'),
        managePortManageRouteTip: this.get('common.hardware_task_route_ethPort_error_tips'),
        bondPortTip: this.get('common.hardware_task_bond_error_tips'),
        unbondPortTip: this.get('common.hardware_task_disablebond_error_tips'),
        manageRouteTip: this.get('common.hardware_task_manageRoute_error_tips1'),
        withIpCanSupportTip: this.get('common.hardware_task_manageRoute_error_tips'),
        unavailableBondTips: this.get('common.hardware_task_unavailablebond_error_tips'),
        portsInSameNetSpaceAreAllowedTips: this.get('system.network_netSpace_createBondPort_err_tip'),
        diskModel: this.get('system.hardware_term_diskMode_label'),
        vendor: this.get('system.hardware_term_Manufacturer_label'),
        MemoryCapacity: this.get('system.hardware_term_memory_cap_col'),
        frequency: this.get('system.hardware_term_memory_frequency_col'),
        diskPosition: this.get('system.hardware_term_locateDisk'),
        diskUnposition: this.get('system.hardware_term_cancelLocateDisk'),
        diskErase: this.get('system.systemSetting_setEncryptDrive_term_dataErasure_button'),
        portProperty: this.get('system.common_term_property_button'),
        errorStatistics: this.get('system.hardware_term_errorStatistic'),
        routingManagement: this.get('system.hardware_manageRoute_task_title_wintitle'),
        bondPort: this.get('system.hardware_term_bindv'),
        bondPorts: this.get('system.network_term_bondPorts_tab'),
        unbondPort: this.get('system.hardware_term_cancelBonding_button'),
        deleteBondPort: this.get('system.systemSetting_network_task_deleteBondPort_title'),
        manageLifPort: this.get('system.hardware_term_ManageLogicPort_tab'),
        clearBtn: this.get('common.clear_button'),
        controllerId: this.get('system.hardware_term_controllerID_label'),
        intfModuleId: this.get('system.hardware_term_interfaceModuleIDv'),
        portId: this.get('system.hardware_term_portID'),
        linkStatus: this.get('system.hardware_term_linkStatus_label'),
        startTime: this.get('common.startTime_label'),
        lostSignals: this.get('system.hardware_term_lostSignalAcount_column'),
        linkBitErrors: this.get('system.hardware_term_lineErrorCodeAcount_column'),
        lostSync: this.get('system.hardware_term_SynLost_column'),
        faildConnections: this.get('system.hardware_term_failConnection_column'),
        crcErrors: this.get('system.dashboard_term_crcErrorNum_tab'),
        frameEndSignErrors: this.get('system.dashboard_term_endFrameErrorNum_tab'),
        errorPackets: this.get('system.hardware_term_errorPackage_column'),
        lostPackets: this.get('system.hardware_term_lostPackage_column'),
        overflowedPackets: this.get('system.hardware_term_floodPackage_column'),
        frameErrors: this.get('system.dashboard_term_frameErrorNum_tab'),
        frameLengthErrors: this.get('system.dashboard_term_frameLengthErrorNum_tab'),
        invalidDwords: this.get('system.hardware_term_invalidDword_column'),
        ConsistencyErrors: this.get('system.hardware_term_disparityError_column'),
        lostSyncDwords: this.get('system.hardware_term_lostDwordSynAcount_column'),
        PHYRestFailures: this.get('system.hardware_term_failPHYResetAcount_column'),
        closeBtn: this.get('common.close_button'),
        viewLif: this.get('system.hardware_term_viewLogicPort_tab'),
        createBtn: this.get('common.create_button'),
        deleteBtn: this.get('common.delete_button'),
        ip: this.get('system.hardware_term_IP_column'),
        subnetMask: this.get('common.term_subNetworkMask'),
        prefix: this.get('common.preix_label'),
        subnetMaskOrPrefix: this.get('system.dataProtection_dirReplication_term_subnetMaskPrefix_label'),
        gateway: this.get('system.hardware_term_gateway'),
        owningEthPort: this.get('system.hardware_term_owningEthPort_tab'),
        createLif: this.get('system.systemSetting_network_task_createLIF_button'),
        editLifTitle: this.get('编辑lif端口'),
        deleteLif: this.get('system.systemSetting_network_task_deleteLIF_button'),
        advance: this.get('common.advance_label'),
        pleaseSelect: this.get('common.select_lab'),
        portType: this.get('common.portType_label'),
        homesiteLab: this.get('主站点'),
        localSiteLab: this.get('local'),
        remoteSiteLab: this.get('remote'),
        ipType: this.get('system.systemSetting_network_task_createLIF_para_IPType_label'),
        okBtn: this.get('common.confirm_button'),
        cancelBtn: this.get('common.cancel_button'),
        createBondPort: this.get('system.systemSetting_network_task_createBondPort_title_wintitle'),
        intfModule: this.get('system.EnumDefine_MOTYPE_INTF_MODULE'),
        availablePorts: this.get('system.port_term_availablePort_tab'),
        selectedPorts: this.get('system.port_term_selectedPort_tab'),
        netSpace: this.get('system.network_netSpace_title'),
        notConnectPortTip: this.get('host.portGroup_task_create_showNoConnectPort_desc'),
        maxTransUnit: this.get('system.device_controlEnsoure_view_Ethport_maxTransUnit'),
        addBtn: this.get('common.add_button'),
        destination: this.get('system.hardware_term_targetAddress'),
        clearPortError: this.get('system.hardware_term_clearBitError_label'),
        addRoute: this.get('system.hardware_term_addRoute_button'),
        deleteRoute: this.get('system.hardware_term_deleteRoute_button'),
        dataErasureMechanism: this.get('system.systemSetting_setEncryptDrive_term_dataErasureMechanism_lab'),
        dataErasureStandard: this.get('system.systemSetting_setEncryptDrive_term_dataErasureStandard_lab'),
        validateSwitch: this.get('system.systemSetting_setEncryptDrive_term_eraseVerify_lab'),
        validatePercent: this.get('system.systemSetting_setEncryptDrive_term_eraseCapacityRatio_lab'),
        pattern: this.get('system.systemSetting_setEncryptDrive_term_dataErasure_patternList_lab'),
        eraseTimes: this.get('system.systemSetting_setEncryptDrive_term_erasingOverwriteTime_lab'),
        eraseProgess: this.get('system.systemSetting_setEncryptDrive_term_dataEraseProgess_label'),
        locationWithColon: this.get('common.location_column', true),
        showEnclosureLocation: this.get('system.hardware_term_showEnclosureLocations_lab'),
        managePort: this.get('system.device_term_managementPort_button'),
        maintenancePort: this.get('system.device_term_maintenancePort_button'),
        modifyBtn: this.get('common.modify_button'),
        modifyLif: this.get('system.systemSetting_network_task_modifyLogicalPort_tittle'),
        rotateBtnTip: this.get('system.hardware_term_turn_button'),
        manageVlanPort: this.get('system.hardware_term_manageVLAN_tittle'),
        createVlan: this.get('system.hardware_term_createVLAN_tittle'),
        modifyVlan: this.get('system.hardware_term_modifyVLAN_tittle'),
        deleteVlan: this.get('system.hardware_term_deleteVLAN_tittle'),
        newId: this.get('system.hardware_term_createVLAN_newID_lab'),
        existId: this.get('system.hardware_term_createVLAN_existID_lab'),
        modifyEnclosureInfoTip: this.get('system.hardware_term_modifyEnclosure_tip'),
        modifyEnclosureInfoTitle: this.get('system.hardware_task_modifyEnclosure_tittle'),
        newName: this.get('system.hardware_task_customName_colunm'),
        cabinet: this.get('system.Bay_label'),
        option: this.get('common.operation_button'),
        modifyEnclosureInfoAnsycTip: this.get('system.enclosure_modifying_info_msg'),
        nvmeTitle: this.get('common.NVMeoF_RoCE_lab'),
        powerManageBtn: this.get('system.hardware_term_PowerManage_btn'),
        powerModelLab: this.get('system.hardware_term_PowerMode_lab'),
        workTypeLabel: this.get('system.hardware_term_PowerSettings_lab'),
        mainOrStandbyTypeLabel: this.get('system.hardware_term_PowerMode_energySave_value'),
        equalizedTypeLabel: this.get('system.hardware_term_PowerMode_loadBalance_value'),
        mainPowerLabel: this.get('system.hardware_term_PrimaryPower_lab'),
        powerLabel: this.get('system.hardware_term_PrimaryPower_lab'),
        modifyEnclosureMsg: this.get('system.hardware_term_PowerManage_btn'),
        modifyPowerSetting: this.get('system.hardware_term_PowerManage_btn'),
        workTypeTip: this.get('system.hardware_term_PowerMode_tip'),
        powerManageBtnBisabledTip: this.get('system.hardware_term_PowerManage_err_tip'),
        modifyPowerManageRest: (name: string): string => this.i18n.get('rest.modifyEnclosurePowerMode_msg', [name]),
        /**
         * 已启用
         */
        snsdBtnOn: this.i18n.get('common.enabled_switch_column'),
        /**
         * 未启用
         */
        snsdBtnOff: this.i18n.get('common.disabled_switch_column'),
        /**
         * 启用后，外部网元可通过该逻辑端口IP地址访问存储系统的DNS服务。
         */
        listenDNSQueryReqTip: this.i18n.get('system.logicPort_task_create_listenDNSRequest_tip'),
        /**
         * 批量配置SNSD
         */
        configSNSDBtn: this.i18n.get('system.network_roce_task_batchConfigSNSD_btn'),
        /**
         * 逻辑类型均为前端端口时才支持该操作。
         */
        configSNSDError: this.i18n.get('system.network_roce_task_batchConfigSNSD_error_tip'),
        /**
         * 启用SNSD
         */
        enablegSNSDTitle: this.i18n.get('system.network_roce_task_enablegSNSD_title'),
        /**
         * 禁用SNSD
         */
        disablegSNSDTitle: this.i18n.get('system.network_roce_task_disablegSNSD_title'),
        /**
         * 您将要执行启用RoCE端口SNSD功能的操作。<br/>
         * 启用SNSD功能开关后，存储设备会向交换机发出LLDP报文，若交换机不支持，可能会有错误报文统计导致的告警。<br/>
         * 建议：执行此操作前，请确保交换机支持此特性。
         */
        enableSNSD: this.i18n.get('system.network_eth_enableSNSD_confirm_msg'),
        /**
         * 您将要执行禁用RoCE端口SNSD功能的操作。<br/>
         * 禁用SNSD功能开关后，将不支持自动建链和故障快速感知功能。<br/>
         * 建议：执行此操作之前，请确认已了解此操作的风险。
         */
        disableSNSD: this.i18n.get('system.network_eth_disableSNSD_confirm_msg'),
        /**
         * 启用SNSD（Storage Network Smart Discovery）后，主机会感知到RoCE端口下所有逻辑端口的状态变化，帮助主机根据逻辑端口状态去判断是否自动建立或断开连接。
         */
        SNSDTip: this.i18n.get('system.network_roce_task_SNSD_tip'),
        /**
         * 批量启用SNSD
         */
        batchEnableSNSD: this.i18n.get('system.network_roce_task_batchEnableSNSD_btn'),
        /**
         * 批量禁用SNSD
         */
        batchDisableSNSD: this.i18n.get('system.network_roce_task_batchDisableSNSD_btn'),
        /**
         * SNSD均已启用。
         */
        batchEnableSNSDTip: this.i18n.get('system.network_roce_task_batchEnableSNSD_error_tip'),
        /**
         * SNSD均已禁用。
         */
        batchDisableSNSDTip: this.i18n.get('system.network_roce_task_batchDisableSNSD_error_tip'),
        /**
         * ID
         */
        idLab: this.get('common.ID_column'),
        /**
         * 修改标题
         */
        modifyWinTitle: (name: string): string => this.i18n.get('system.device_term_modifyServicePort', [name]),
        getHomePortName: (params: string): string => this.i18n.get('common.variable_withBracket_lab', [params]),
        modifySnsdRest: (name: string): string => this.i18n.get('rest.modify_port_msg', [name]),
        /**
         * block_erase擦除机制提示
         */
        blockDes: this.get('system.systemSetting_setEncryptDrive_term_dataErasureMechanism_block_des'),
        /**
         * cryptographic_erase擦除机制提示
         */
        crytographicDes: this.get('system.systemSetting_setEncryptDrive_term_dataErasureMechanism_crytographic_des'),
        /**
         * overwrite擦除机制提示
         */
        overwriteDes: this.get('system.systemSetting_setEncryptDrive_term_dataErasureMechanism_overwrite_des'),
        /**
         * 激活
         */
        activeLab: this.get('common.active_button'),
        noPortErrTip: this.get('common.selectOnlyOneObject_lab'),

        // IPsec
        removeBtnLab: this.get('common.remove_button'),
        manageIPsecPolicy: this.get('system.logicPort_task_manageIPSecPolicy_btn'),
        remoteIPLab: this.get('system.logicPort_task_addIPSecPolicy_remoteIP_lab'),
        encryptProtocolLabel: this.get('settings.snmpProtocol_term_addUser_encryptProtocol_label'),
        preKeyLab: this.get('system.logicPort_task_addIPSecPolicy_preKey_lab'),
        IPsecPolicySessionStatusLab: this.get('system.logicPort_task_addIPSecPolicy_sessionStatus_lab'),
        remoteIPRepeatNumTip: this.i18n.get('common.allCanNotEquall_msg', [this.get('system.logicPort_task_addIPSecPolicy_remoteIP_lab')]),
        manageIPSecPolicyDisabledtip: this.i18n.get('system.logicPort_task_manageIPSecPolicy_err_tip'),

        // 绑定端口
        bondPortType: this.get('compute.service_network_BindPorts_lab'),
        bondMode: this.get('compute.service_network_BindMode_lab'),
        bondPortDisableTips: this.get('compute.service_network_NoDelete_tip'),

        // 虚拟交换机
        vSwitchTab: this.get('compute.service_network_vSwitch_tab'),

        // Dashboard
        dashboardTab: this.get('common.dashboard_perf_static_title'),

        // 网络管理
        networkManagerTitle: this.get('settings.networkConfig_simplifiedMode_title'),

        comma: this.get('common.comma_label'),

        // 术语变更 riser-->carrier-->box
        carrierErrorRegularTip: (faultMembersName: string): string => this.get('system.hardware_term_riser_fault_info1', [faultMembersName]),
        carrierErrorLinkTip: this.get('system.hardware_term_riser_fault_info2'),
        eightCardsCarrier: this.get('system.hardware_term_riserModel_8interface_value'),
        thirtyTwoDisksCarrier: this.get('system.hardware_term_riserModel_32slot_value'),
        // 归属交换机
        belongSwitchLab: this.get('system.hardware_term_SOSwitchPorts_belongSwitch_col'),
        // 交换机Tip
        switchTip: this.get('system.hardware_term_SOSwitch_tip'),
    };

    supportSmartThin = this.toolService.supportSmartThinLicense();

    intfModuleModel = this.getIntfModuleModel();

    enclosureModel = this.getI18n({
        39: 'common.EnumDefine_ENCLOSURE_MODEL_E_EXPSAS4U_75_12G',
        65: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_SAS_2U_25',
        67: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_SAS_2U_25',
        66: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_SAS_4U_24',
        69: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_SAS_4U_24',
        112: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_4U',
        113: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_DOUBLE_SAS_2U_25',
        114: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_DOUBLE_SAS_2U_12',
        115: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_DOUBLE_NVMe_2U_36',
        116: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_DOUBLE_SAS_2U_25',
        117: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_DOUBLE_SAS_2U_12',
        118: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_IP_SAS_2U_25',
        119: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_IP_SAS_2U_12',
        120: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_IP_NVMe_2U_36',
        122: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_DOUBLE_NVMe_2U_25',
        130: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_DOUBLE_SAS_2U_25',
        131: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_DOUBLE_SAS_2U_12',
        132: 'compute.system_device_controllerEnclosure_label',
        133: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_4U_12',
        135: 'compute.EnumDefine_ENCLOSURE_MODEL_E_CTRL_DOUBLE_4U_10_25',
        137: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_DOUBLE_NVMe_CONTROLLER_8U_64',
        139: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_DOUBLE_NVMe_CONTROLLER_2U_36',
        140: 'common.EnumDefine_ENCLOSURE_MODEL_E_CTRL_DOUBLE_NVMe_CONTROLLER_2U_36',
    });

    // 需要伪装的接口卡映射表
    intfModuleModelMap = this.getI18n({
        583: 'system.EnumDefine_INTF_MODEL_E_SAS_V2_4',
    });

    enclosureLogicType = {
        0: this.i18nLabel.diskEnclosure,
        1: this.i18nLabel.ctrlEnclosure,
        2: this.i18nLabel.dswEnclosure,
    };

    i18nType = {
        enclosure: this.enclosureLogicType,
        intfModule: this.intfModuleModel,
        bay: this.i18nLabel.bay,
        controller: this.i18nLabel.controller,
        expboard: this.i18nLabel.expboard,
        power: this.i18nLabel.power,
        backupPower: this.i18nLabel.BBU,
        fan: this.i18nLabel.fan,
        serialPort: this.i18nLabel.serialPort,
        sasPort: this.i18nLabel.sasPort,
        fcPort: this.i18nLabel.fcPort,
        fanCtrlModule: this.i18nLabel.fanCtrlModule,
        disk: this.i18nLabel.disk,
        ethPort: this.i18nLabel.ethPort,
        riser: this.i18nLabel.riser,
        // 术语变更 riser-->carrier-->box
        carrier: this.i18nLabel.carrier,
        managementBoard: this.i18nLabel.managementBoard,
        computeNode: this.i18nLabel.computeNode,

        ethPortGroup: this.i18nLabel.ethPort,
        serialPortGroup: this.i18nLabel.serialPort,
        fcPortGroup: this.i18nLabel.fcPort,
        sasPortGroup: this.i18nLabel.sasPort,
        fanGroup: this.i18nLabel.fanGroup,
        powerGroup: this.i18nLabel.powerGroup,
        backupPowerGroup: this.i18nLabel.BBU,
    };

    healthStatusOpt = this.getI18n({
        1: 'common.EnumDefine_STATUS_E_NORMAL',
        2: 'common.EnumDefine_HEALTH_STATUS_fault',
        3: 'common.EnumDefine_HEALTH_STATUS_E_PRE_FAIL',
        14: 'common.EnumDefine_HEALTH_STATUS_E_INVALID',
    });

    runningStatusOpt = this.getI18n({
        1: 'common.EnumDefine_STATUS_E_NORMAL',
        23: 'common.EnumDefine_RUNNING_STATUS_E_SYNCHRONIZING',
        26: 'protection.replicationCG_split_button',
        33: 'common.EnumDefine_RUNNING_STATUS_E_TO_BE_RECOVERD',
        34: 'common.EnumDefine_RUNNING_STATUS_E_INTERRUPTED',
        35: 'common.EnumDefine_CMO_LICENSE_STATUS_4',
        // 110: 'common.EnumDefine_RUNNING_STATUS_E_STANDBY'
    });

    /**
     * 健康状态图标
     */
    healthStatusSvg = {
        1: 'normal',
        2: 'faulty',
    };
    /**
     * 逻辑端口-运行状态
     */
    lifPortRunningStatusOpt = this.getI18n({
        0: 'common.EnumDefine_RUNNING_STATUS_E_UNKNOWN',
        10: 'common.EnumDefine_RUNNING_STATUS_E_LINK_UP',
        11: 'common.EnumDefine_RUNNING_STATUS_E_LINK_DOWN',
        33: 'common.EnumDefine_RUNNING_STATUS_E_TO_BE_RECOVERD',
        110: 'common.EnumDefine_RUNNING_STATUS_E_TO_BE_WORK',
    });

    /**
     * 框-运行状态
     */
    enclosureRunningStatusOpt = this.getI18n({
        0: 'common.EnumDefine_RUNNING_STATUS_E_UNKNOWN',
        1: 'common.EnumDefine_RUNNING_STATUS_E_NORMAL',
        2: 'common.EnumDefine_RUNNING_STATUS_E_RUNNING',
        5: 'common.EnumDefine_RUNNING_STATUS_E_HIGH_TEMPERATURE_SLEEP',
        27: 'common.EnumDefine_RUNNING_STATUS_E_ONLINE',
        28: 'common.EnumDefine_RUNNING_STATUS_E_OFFLINE',
    });

    /**
     * 框-健康状态
     */
    enclosureHealthStatusOpt = this.getI18n({
        0: 'common.EnumDefine_HEALTH_STATUS_E_UNKNOWN',
        1: 'common.EnumDefine_STATUS_E_NORMAL',
        2: 'common.EnumDefine_HEALTH_STATUS_fault',
    });

    /**
     * 控制器-运行状态
     */
    controllerRunningStatusOpt = this.getI18n({
        0: 'common.EnumDefine_RUNNING_STATUS_E_UNKNOWN',
        1: 'common.EnumDefine_RUNNING_STATUS_E_NORMAL',
        2: 'common.EnumDefine_RUNNING_STATUS_E_RUNNING',
        27: 'common.EnumDefine_RUNNING_STATUS_E_ONLINE',
        28: 'common.EnumDefine_RUNNING_STATUS_E_OFFLINE',
    });

    /**
     * 控制器-健康状态
     */
    controllerHealthStatusOpt = this.getI18n({
        0: 'common.EnumDefine_HEALTH_STATUS_E_UNKNOWN',
        1: 'common.EnumDefine_STATUS_E_NORMAL',
        2: 'common.EnumDefine_HEALTH_STATUS_fault',
        9: 'common.EnumDefine_HEALTH_STATUS_E_INCONSISTENT',
    });

    basic5910HealthStatusOpt = this.getI18n({
        0: 'common.EnumDefine_HEALTH_STATUS_E_UNKNOWN',
        1: 'common.EnumDefine_STATUS_E_NORMAL',
        2: 'common.EnumDefine_HEALTH_STATUS_fault',
    });

    basic5910RunningStatusOpt = this.getI18n({
        27: 'common.EnumDefine_RUNNING_STATUS_E_ONLINE',
        28: 'common.EnumDefine_RUNNING_STATUS_E_OFFLINE',
    });

    /**
     * 管理板-健康状态
     */
    mgmtHealthStatusOpt = this.basic5910HealthStatusOpt;

    /**
     * 管理板-运行状态
     */
    mgmtRunningStatusOpt = this.basic5910RunningStatusOpt;

    /**
     * 计算节点-健康状态
     */
    computeNodeHealthStatusOpt = this.basic5910HealthStatusOpt;

    /**
     * 计算节点-运行状态
     */
    computeNodeRunningStatusOpt = this.basic5910RunningStatusOpt;

    /**
     * CPU-健康状态
     */
    cpuHealthStatusOpt = this.basic5910HealthStatusOpt;

    /**
     * 内存-健康状态
     */
    memoryHealthStatusOpt = this.basic5910HealthStatusOpt;

    /**
     * 以太网端口-运行状态
     */
    ethPortRunningStatusOpt = this.getI18n({
        0: 'common.EnumDefine_RUNNING_STATUS_E_UNKNOWN',
        10: 'common.EnumDefine_RUNNING_STATUS_E_LINK_UP',
        11: 'common.EnumDefine_RUNNING_STATUS_E_LINK_DOWN',
        ...(FeatureConfig.IsHideEthPortRecoverdRunningStatus ? {} : { 33: 'common.EnumDefine_RUNNING_STATUS_E_TO_BE_RECOVERD' }),
    });

    /**
     * 以太网端口-健康状态
     */
    ethPortHealthStatusOpt = this.getI18n({
        0: 'common.EnumDefine_HEALTH_STATUS_E_UNKNOWN',
        1: 'common.EnumDefine_STATUS_E_NORMAL',
        2: 'common.EnumDefine_HEALTH_STATUS_fault',
        7: 'common.EnumDefine_HEALTH_STATUS_E_HAS_ERR_CODE',
    });

    /**
     * FC端口-运行状态
     */
    fcPortRunningStatusOpt = this.getI18n({
        0: 'common.EnumDefine_RUNNING_STATUS_E_UNKNOWN',
        10: 'common.EnumDefine_RUNNING_STATUS_E_LINK_UP',
        11: 'common.EnumDefine_RUNNING_STATUS_E_LINK_DOWN',
    });

    /**
     * FC端口-健康状态
     */
    fcPortHealthStatusOpt = this.getI18n({
        0: 'common.EnumDefine_HEALTH_STATUS_E_UNKNOWN',
        1: 'common.EnumDefine_STATUS_E_NORMAL',
        2: 'common.EnumDefine_HEALTH_STATUS_fault',
        7: 'common.EnumDefine_HEALTH_STATUS_E_HAS_ERR_CODE',
    });

    /**
     * Bond端口-运行状态
     */
    bondPortRunningStatusOpt = this.getI18n({
        0: 'common.EnumDefine_RUNNING_STATUS_E_UNKNOWN',
        10: 'common.EnumDefine_RUNNING_STATUS_E_LINK_UP',
        11: 'common.EnumDefine_RUNNING_STATUS_E_LINK_DOWN',
        33: 'common.EnumDefine_RUNNING_STATUS_E_TO_BE_RECOVERD',
    });

    /**
     * Bond端口-健康状态
     */
    bondPortHealthStatusOpt = this.getI18n({
        0: 'common.EnumDefine_HEALTH_STATUS_E_UNKNOWN',
        1: 'common.EnumDefine_STATUS_E_NORMAL',
        2: 'common.EnumDefine_HEALTH_STATUS_fault',
        4: 'common.EnumDefine_HEALTH_STATUS_E_PART_BROKEN',
        7: 'common.EnumDefine_HEALTH_STATUS_E_HAS_ERR_CODE',
    });

    /**
     * 交换机-健康状态
     */
    switchHealthStatusOpt = this.getI18n({
        [healthStatusE.Normal.value]: 'common.EnumDefine_STATUS_E_NORMAL',
        [healthStatusE.Fault.value]: 'common.EnumDefine_HEALTH_STATUS_fault',
    });

    /**
     * 交换机端口-状态
     */
    switchPortStatusOpt = this.getI18n({
        [SwitchPortStatusE.LINK_DOWN.value]: 'common.EnumDefine_RUNNING_STATUS_E_LINK_DOWN',
        [SwitchPortStatusE.LINK_UP.value]: 'common.EnumDefine_RUNNING_STATUS_E_LINK_UP',
    });

    /**
     * Bond端口类型-适用dorado和新融合小驼峰字段-由VXLAN开关控制-bondPortType
     */
    bondPortCamelCaseOpt = this.getI18n({
        [BondPortTypeE.HOST]: 'system.systemSetting_network_task_createBondPort_host_value',
        [BondPortTypeE.CLOUD_VXLAN]: 'system.systemSetting_network_task_createBondPort_cloudvxlan_value',
    });

    /**
     * Bond端口类型-适用计算型存储支持的全大写字段-BONDPORTTYPE
     */
    bondPortOpt = this.getI18n({
        [BondPortTypeE.HOST]: 'compute.service_network_host_value',
        [BondPortTypeE.SWITCH_PORT]: 'compute.service_network_switch_value',
    });

    /**
     * Bond端口-绑定模式
     */
    bondModesOpt = this.getI18n({
        1: 'compute.service_network_BindMode_value',
        4: 'compute.service_network_DynamicLinkAggregation_value',
    });

    /**
     * sas 端口运行状态
     */
    sasRunningStatusOpt = this.getI18n({
        10: 'common.EnumDefine_RUNNING_STATUS_E_LINK_UP',
        11: 'common.EnumDefine_RUNNING_STATUS_E_LINK_DOWN',
    });

    /**
     * VLAN端口-运行状态
     */
    vlanRunningStatusOpt = this.getI18n({
        0: 'common.EnumDefine_RUNNING_STATUS_E_UNKNOWN',
        10: 'common.EnumDefine_RUNNING_STATUS_E_LINK_UP',
        11: 'common.EnumDefine_RUNNING_STATUS_E_LINK_DOWN',
        33: 'common.EnumDefine_RUNNING_STATUS_E_TO_BE_RECOVERD',
    });

    statusOpt = this.getI18n({
        0: 'common.EnumDefine_RUNNING_STATUS_E_UNKNOWN',
        1: 'common.EnumDefine_RUNNING_STATUS_E_NORMAL',
        2: 'common.EnumDefine_RUNNING_STATUS_E_RUNNING',
        10: 'common.EnumDefine_RUNNING_STATUS_E_LINK_UP',
        11: 'common.EnumDefine_RUNNING_STATUS_E_LINK_DOWN',
        33: 'common.EnumDefine_RUNNING_STATUS_E_TO_BE_RECOVERD',
        53: 'common.EnumDefine_RUNNING_STATUS_INITIALIZING',
        110: 'common.EnumDefine_RUNNING_STATUS_E_TO_BE_WORK',
    });

    fcRunMode = {
        0: 'Fabric',
        1: 'FC-AL',
        2: 'P2P',
        3: 'Auto',
    };

    portSwitchOpt = {
        false: this.get('common.service_disable_value'),
        true: this.get('common.service_enable_value'),
    };

    diskEncryptType = {
        0: this.get('system.hardware_term_encryType_commonDisk_value'),
        1: this.get('system.hardware_term_encryType_encryptedDisk_value'),
    };

    diskRoleOpt = {
        1: this.get('common.EnumDefine_DISK_LOGIC_TYPE_E_FREE'),
        2: this.get('common.EnumDefine_DISK_LOGIC_TYPE_E_MEMBER'),
        3: this.get('common.EnumDefine_DISK_LOGIC_TYPE_E_SPARE'),
        4: this.get('common.EnumDefine_DISK_LOGIC_TYPE_E_CACHE'),
    };

    routeTypeOpt = {
        0: this.get('system.EnumDefine_ENUM_ROUTE_TYPE_E_ROUTE_TYPE_NET'),
        1: this.get('system.EnumDefine_ENUM_ROUTE_TYPE_E_ROUTE_TYPE_HOST'),
        2: this.get('system.EnumDefine_ENUM_ROUTE_TYPE_E_ROUTE_TYPE_DEFAULT'),
    };

    // 微存储支持前端口、管理口、维护口、Scale-Out互联端口和级联端口
    logicTypeMicroOpt = {
        0: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_SERVICE'),
        1: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_EXP'),
        2: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_MNGT'),
        4: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_MAINTENANCE'),
        11: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_IP_SCALE_OUT'),
    };
    logicTypeOpt = {
        0: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_SERVICE'),
        2: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_MNGT'),
        11: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_IP_SCALE_OUT'),
        // 计算型存储屏蔽级联端口、维护端口
        ...(FeatureConfig.IsShowVSwitch ? { 16: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_SWITCH') } : {}),
        ...(FeatureConfig.IsShowExpPortType ? { 1: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_EXP') } : {}),
        ...(FeatureConfig.IsShowMaintenancePortType ? { 4: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_MAINTENANCE') } : {}),
        // 支持容器特性显示容器前端端口、后端端口
        ...(this.toolService.hasContainerEntryLicense() ?
            {
                13: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_CONTAINER_FRONT'),
                14: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_CONTAINER_BACK'),
            } : {}
        ),
    };

    // 微存储虚拟机场景roce端口下拉框过滤
    roceLogicTypeVmOpt = {
        0: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_SERVICE'),
        ...(this.toolService.hasContainerEntryLicense() ?
            {
                13: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_CONTAINER_FRONT'),
                14: this.get('system.EnumDefine_PORT_LOGIC_TYPE_E_CONTAINER_BACK'),
            } : {}
        ),
    };

    trustOpt = {
        0: this.get('system.network_roce_view_trustMode_PCP_value'),
        1: this.get('system.network_roce_view_trustMode_DSCP_value'),
    };

    portTypeOpt = {
        eth: this.get('common.EnumDefine_MOTYPE_ETH_PORT1'),
        bond: this.get('common.bondPort_value'),
        vlan: this.get('VLAN'),
        sip: this.get('system.logicPort_task_create_portType_SIP_value'),
    };

    portVlanTypeOpt = {
        eth: this.get('common.EnumDefine_MOTYPE_ETH_PORT1'),
        roce: this.get('common.EnumDefine_MOTYPE_RoCE_PORT'),
        bond: this.get('common.bondPort_value'),
    };

    controllerRoleOpt = {
        0: this.get('system.EnumDefine_SYSTEM_ROLE_E_NORMAL'),
        1: this.get('system.EnumDefine_SYSTEM_ROLE_E_MASTER'),
        2: this.get('system.EnumDefine_SYSTEM_ROLE_E_SLAVE'),
    };

    networkTypeOpts: SelectOption[] = [
        {
            label: this.get('common.LogicalPorts'),
            value: MO.LIF_PORT_TYPE,
        },
        {
            label: this.get('system.network_eth_tiitle'),
            value: MO.ETH_PORT_TYPE,
        },
        ...(FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif ? [
            {
                label: this.get('system.network_roce_title'),
                value: MO.NVMeoF_RoCE_TYPE,
            },
        ] : []),
        {
            label: this.get('system.network_fc_tiitle'),
            value: MO.FC_PORT_TYPE,
        },
    ];

    lifProtocolOpt = {
        [LifProtoCol.NONE]: this.get('common.term_confirm_none'),
        [LifProtoCol.NFS]: this.get('NFS'),
        [LifProtoCol.CIFS]: this.get('CIFS'),
        [LifProtoCol.NFSAndCIFS]: this.get('NFS + CIFS'),
        [LifProtoCol.ISCSI]: this.get('iSCSI'),
        [LifProtoCol.FC_FCoE]: this.get('FC/FCoE'),
        [LifProtoCol.NVMe_Over_RoCE]: this.get('common.NVMeoF_RoCE_lab'),
        [LifProtoCol.BGP]: this.get('common.BGP_lab'),
        [LifProtoCol.DataTurbo]: this.get('common.DataTurbo_lab'),
        [LifProtoCol.DataTurbo_Over_RoCE]: this.i18nLabel.DataTurboOverRoceLab,
        [LifProtoCol.NFS_over_RDMA]: this.get('settings.fileStorageService_term_nfsService_NFSoverRDMA_lab'),
        [LifProtoCol.S3]: this.get('service.Cloudtier_task_remoteIPGroup_create_S3_value'),
    };

    /**
     * 角色
     */
    lifRoleOption: SelectOption[] = [
        {
            label: this.get('system.logicPort_task_create_role_management_value'),
            value: 1,
        },
        {
            label: this.get('system.logicPort_task_create_role_service_value'),
            value: 2,
        },
        {
            label: this.get('system.logicPort_task_create_role_serviceAndManagement_value'),
            value: 3,
        },
        {
            label: this.get('system.logicPort_task_create_role_replication_value'),
            value: 4,
        },
        {
            label: this.get('system.logicPort_task_create_role_client_value'),
            value: 8,
        },
    ];

    fcProtocolOpt = {
        1: this.get('system.FCPort_task_modify_protocolType_SCSI_value'),
        2: this.get('system.FCPort_task_modify_protocolType_NVMe_value'),
    };

    /** 立即数 */
    fastWriteEnableOpt = {
        0: this.i18n.get('common.disabled_switch_column'),
        1: this.i18n.get('common.enabled_switch_column'),
    };

    lifProtocolOptions: SelectOption[] = [
        {
            label: this.lifProtocolOpt[LifProtoCol.NONE],
            value: LifProtoCol.NONE,
        },
        ...(SWITCH.SUPPORT_NAS ? [
            {
                label: this.lifProtocolOpt[LifProtoCol.NFS],
                value: LifProtoCol.NFS,
            },
            ...(FeatureConfig.IsHideCifsLif ? [] :
                [{
                    label: this.lifProtocolOpt[LifProtoCol.CIFS],
                    value: LifProtoCol.CIFS,
                }]),
            ...(FeatureConfig.IsHideNfsAndCifsLif ? [] :
                [{
                    label: this.lifProtocolOpt[LifProtoCol.NFSAndCIFS],
                    value: LifProtoCol.NFSAndCIFS,
                }]),
            {
                label: this.lifProtocolOpt[LifProtoCol.NFS_over_RDMA],
                value: LifProtoCol.NFS_over_RDMA,
            },
        ] : []),
        // 微存储直通只支持roce
        ...(!SWITCH.IS_DIRECT_MODE && this.supportSmartThin && !FeatureConfig.IsHideIscsiLif ? [
            {
                label: this.lifProtocolOpt[LifProtoCol.ISCSI],
                value: LifProtoCol.ISCSI,
            },
        ] : []),
        ...(FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif && this.supportSmartThin && !FeatureConfig.IsHideNVMEOverRoCELif ? [
            {
                label: this.lifProtocolOpt[LifProtoCol.NVMe_Over_RoCE],
                value: LifProtoCol.NVMe_Over_RoCE,
            },
        ] : []),
        ...(FeatureConfig.IsSupportBGPLif ? [
            {
                label: this.lifProtocolOpt[LifProtoCol.BGP],
                value: LifProtoCol.BGP,
            },
        ] : []),

        ...(this.isSupportDataTurboLif() ? [
            {
                label: this.lifProtocolOpt[LifProtoCol.DataTurbo],
                value: LifProtoCol.DataTurbo,
            },
        ] : []),
        ...(this.isSupportDataTurboOverRoceLif() ? [
            {
                label: this.lifProtocolOpt[LifProtoCol.DataTurbo_Over_RoCE],
                value: LifProtoCol.DataTurbo_Over_RoCE,
            },
        ] : []),
        ...(ProductConfig.isSupportS3Protocol ? [
            {
                label: this.lifProtocolOpt[LifProtoCol.S3],
                value: LifProtoCol.S3,
            },
        ] : []),
    ];

    lifRoleOpt = {
        1: this.get('system.logicPort_task_create_role_management_value'),
        2: this.get('system.logicPort_task_create_role_service_value'),
        3: this.get('system.logicPort_task_create_role_serviceAndManagement_value'),
        4: this.get('system.logicPort_task_create_role_replication_value'),
        8: this.get('system.logicPort_task_create_role_client_value'),
        [RoleTypeE.VTEP]: this.get('system.logicPort_task_create_role_vtep_value'),
        [RoleTypeE.HEALTH_CHECK]: this.get('system.logicPort_task_create_role_healthCheck_value'),
        [RoleTypeE.DATA_BACKUP]: this.get('system.logicPort_task_create_role_dataBackup_value'),
        [RoleTypeE.SYSTEM_MANAGEMENT]: this.get('system.logicPort_task_create_role_systemAdmin_value'),
    };

    lifFailbackModeOpt = {
        1: this.get('common.EnumDefine_STARTUP_MODE_E_MANUAL'),
        2: this.get('common.EnumDefine_STARTUP_MODE_E_AUTO'),
    };

    lifActiveOpt = {
        true: this.get('common.EnumDefine_RUNNING_STATUS_E_ACTIVATED'),
        false: this.get('common.EnumDefine_RUNNING_STATUS_E_UNACTIVATED'),
    };

    lifFailoverOpt = {
        true: this.get('common.enabled_switch_column'),
        false: this.get('common.disabled_label'),
    };

    mechanismOptions = [
        {
            value: '0',
            label: 'overwrite',
        },
        {
            value: '1',
            label: 'block_erase',
        },
        {
            value: '2',
            label: 'cryptographic_erase',
        },
    ];

    standardOptions = [
        {
            value: '0',
            label: 'DoD 5220.22-M (E)',
        },
        {
            value: '1',
            label: 'DoD 5220.22-M (ECE)',
        },
        {
            value: '2',
            label: 'VSITR',
        },
        {
            value: '3',
            label: 'Custom',
        },
    ];

    validateSwitchOptions = [
        {
            value: '0',
            label: this.get('system.common_term_close_button'),
        },
        {
            value: '1',
            label: this.get('system.common_term_enable_button'),
        },
    ];

    rocePortTypeOptions = {
        value: 'roce',
        label: this.i18nLabel.roceport,
    };

    newDisRoleOpt = {
        1: this.get('common.EnumDefine_DISK_LOGIC_TYPE_E_FREE'),
        2: this.get('common.EnumDefine_DISK_LOGIC_TYPE_E_MEMBER'),
        4: this.get('common.EnumDefine_DISK_LOGIC_TYPE_E_CACHE'),
    };

    // 框的电源工作模式
    curSupplyModeType = {
        [CurSupplyModeTypeE.MAIN_OR_STANDBY]: this.i18n.get('system.hardware_term_PowerMode_energySave_value'),
        [CurSupplyModeTypeE.EQUALIZED]: this.i18n.get('system.hardware_term_PowerMode_loadBalance_value'),
    };

    /** box型号选项 术语变更 riser-->carrier-->box */
    carrierModelOptions = [
        {
            value: CarrierModelE.EIGHT_CARDS,
            label: this.i18nLabel.eightCardsCarrier,
        },
        {
            value: CarrierModelE.THIRTY_TWO_DISKS,
            label: this.i18nLabel.thirtyTwoDisksCarrier,
        },
    ];

    /**
     * 真假容量型SSD名称相同，过滤掉重复选项
     */
    diskTypeOpts = DiskTypeOpts.filter((diskTypeOpt): boolean =>
        diskTypeOpt.value !== DiskTypeE.PHONY_CAPACITY_OPTIMIZED_SSD && diskTypeOpt.value !== DiskTypeE.PHONY_CAPACITY_OPTIMIZED_SSD_SED);
    diskTypeOpttions = this.toolService.convertEnumToOptions(this.diskTypeOpts);
    diskTypeRenderOpttions = this.toolService.convertEnumToOptions(DiskTypeOpts);
    diskRoleOptions = this.obj2Options(this.newDisRoleOpt);
    routeTypeOptions = this.obj2Options(this.routeTypeOpt);
    portTypeOptions = this.obj2Options(this.portTypeOpt);
    portVlanTypeOptions = this.obj2Options(this.portVlanTypeOpt);
    lifRoleOptions = this.obj2Options(this.lifRoleOpt);
    lifActiveOptions = this.obj2Options(this.lifActiveOpt);
    portStatusOptions = this.obj2Options(this.statusOpt);
    sasStatusOptions = this.obj2Options(this.sasRunningStatusOpt);
    enclosureRunningStatusOptions = this.obj2Options(this.enclosureRunningStatusOpt);
    lifPortRunningStatusOptions = this.obj2Options(this.lifPortRunningStatusOpt);
    enclosureHealthStatusOptions = this.obj2Options(this.enclosureHealthStatusOpt);
    controllerRunningStatusOptions = this.obj2Options(this.controllerRunningStatusOpt);
    controllerHealthStatusOptions = this.obj2Options(this.controllerHealthStatusOpt);
    mgmtHealthStatusOptions = this.obj2Options(this.mgmtHealthStatusOpt);
    mgmtRunningStatusOptions = this.obj2Options(this.mgmtRunningStatusOpt);
    computeNodetHealthStatusOptions = this.obj2Options(this.computeNodeHealthStatusOpt);
    computeNodeRunningStatusOptions = this.obj2Options(this.computeNodeRunningStatusOpt);
    cpuHealthStatusOptions = this.obj2Options(this.cpuHealthStatusOpt);
    memoryHealthStatusOptions = this.obj2Options(this.memoryHealthStatusOpt);
    ethPortRunningStatusOptions = this.obj2Options(this.ethPortRunningStatusOpt);
    ethPortHealthStatusOptions = this.obj2Options(this.ethPortHealthStatusOpt);
    fcPortRunningStatusOptions = this.obj2Options(this.fcPortRunningStatusOpt);
    fcPortHealthStatusOptions = this.obj2Options(this.fcPortHealthStatusOpt);
    bondPortRunningStatusOptions = this.obj2Options(this.bondPortRunningStatusOpt);
    bondPortHealthStatusOptions = this.obj2Options(this.bondPortHealthStatusOpt);
    bondPortOptions = this.obj2Options(this.bondPortOpt);
    bondPortCamelCaseOptions = this.obj2Options(this.bondPortCamelCaseOpt);
    bondPortModesOptions = this.obj2Options(this.bondModesOpt);
    vlanRunningStatusOptions = this.obj2Options(this.vlanRunningStatusOpt);
    trustOptions = this.obj2Options(this.trustOpt);
    enclosurePowerModeOptions = this.obj2Options(this.curSupplyModeType);
    switchHealthStatusOptions = this.obj2Options(this.switchHealthStatusOpt);
    switchPortStatusOptions = this.obj2Options(this.switchPortStatusOpt);

    promptTip = {
        createLif: this.get('system.systemSetting_network_task_createLIF_button'),
        modifyLif: this.get('system.systemSetting_network_task_modifyLif_info_message'),
        deleteLif: this.get('system.systemSetting_network_task_deleteRoute_info_new_message'),
        failbackLif: this.get('system.systemSetting_network_task_switchbackLIF_info_message'),
        modifyVlan: (name: string): string => this.get('system.systemSetting_network_task_changevlan_info_message', [name]),
        deleteVlan: this.get('system.systemSetting_network_task_deleteVlan_info_message'),
        bondPort: this.get('system.hardware_bindiSCSI_task_confirmation_message'),
        unbondPort: this.get('system.hardware_deleteBondPort_task_confirmation_message'),
        addRoute: this.get('system.systemSetting_network_task_addRoute_info_message'),
        deleteRoute: this.get('system.systemSetting_network_task_deleteRoute_info_message'),
        clearPortError: this.get('system.hardware_clearBitError_task_info_msg'),
        diskErase: this.get('system.systemSetting_setEncryptDrive_term_dataErasure_msg'),
        diskEraseVerify: this.get('system.systemSetting_setEncryptDrive_term_dataErasure_verify_msg'),
        modifyEnclosureName: this.get('system.hardware_task_modifyDiskEnclousureID_comfirm_msg'),
        modifyEnclosureLocation: this.get('system.hardware_task_modifyEnclosureLoc_comfirm_msg'),
        createLifDangerTip: this.get('system.systemSetting_network_task_createManageLIF_info_message'),
        createLifXSeriesDangerTip: this.get('system.systemSetting_network_task_createManageLIF_XSeries_info_message'),
        createLifNFSOverXXXDangerTip: this.get('system.systemSetting_network_task_createManageLIF_configRoCEandRDMA_confirm_msg'),
        createLifRoleTip: this.get('system.systemSetting_network_task_createManageLIF_RepAndServicePort_confirm_message'),
    };

    disabledTips = {
        defaultLif: this.get('system.systemSetting_setEncryptDrive_term_LIFPortNotSupport_msg'),
        failbacklif: this.get('system.logicPort_task_switchbackPort_error_tip'),
        failbacklifNotSupportNas: this.get('system.logicPort_task_switchback_inactiveNas_err_tip'),
    };

    errorMsg = {
        portNotFound: this.get('system.systemSetting_setEncryptDrive_term_cannotFindBondPort_msg'),
        pattern: this.get('system.systemSetting_setEncryptDrive_term_dataErasure_patternList_value'),
        atLeastOneErr: this.get('settings.userAndRole_roleManagement_task_add_noSelectObject_tip'),
        atLeastTwoErr: this.get('system.systemSetting_network_task_createBondPort_select2Objects_tip'),
        ipFormatErrMsg: this.i18n.get('common.IPAddr_validation'),
        v4MaskErr: this.get('system.device_term_para_format_subnetMaskexample_validation'),
        v4subNetAndPrefixaErr: this.get('system.device_term_para_format_subNetAndPrefix_validation'),
        v6PrefixaErr: this.get('system.device_term_para_format_Prefix_validation'),
        vlanIdFormateErr: this.get('system.hardware_term_createVLAN_ID_info_lab'),
        vlanIdRangeErr: this.get('system.hardware_term_createVLAN_IDRange_info_lab'),
        vlanIdSortErr: this.get('system.hardware_term_createVLAN_batchID_error_info_lab'),
        vlanIdRepeatErr: this.get('system.hardware_term_createVLAN_IDExist_info_lab'),
        enclosureNameErr: this.get('system.hardware_task_customName_value'),
        gatewayErr: this.get('common.input__gatewayRule_msg'),
        v4AddrErr: this.get('common.IPv4Addr_validation'),
        v6AddrErr: this.get('common.IPv6Addr_validation'),
    };

    restTitle = {
        deleteLif: (name: string): string => this.get('rest.deleteLogicPort_msg', [name]),
        createVlan: (name: string): string => this.get('rest.createVLAN_msg', [name]),
        deleteVlan: (name: string): string => this.get('rest.deleteVLAN_msg', [name]),
        locateDisk: (name: string): string => this.get('rest.locateDisk_msg', [name]),
        cancelLocateDisk: (name: string): string => this.get('rest.cancelLocateDisk_msg', [name]),
        modifyEnclosureInfo: (name: string): string => this.get('rest.modifyEnclosureInfo_msg', [name]),
    };

    constructor(
        private i18n: I18nService,
        private toolService: ToolService,
        private permissionService: PermissionService,
    ) { }

    /**是否支持dataTurbo over roce逻辑端口 */
    isSupportDataTurboOverRoceLif(): boolean {
        return this.permissionService.isLicenseExist([LicenseKey.DBStorClient]) || ProductConfig.isSupportDataTurboOverRoceProtocolLif;
    }

    /**是否支持dataTurbo逻辑端口 */
    isSupportDataTurboLif(): boolean {
        return (SWITCH.SUPPORT_NAS && FeatureConfig.IsSupportDataTurboWhenNASIsSupported) || FeatureConfig.IsSupportDataTurboLif;
    }

    nullErrMsg(name: string): string {
        const i18nName = this.i18nLabel[name];
        return this.i18n.get('common.null_validation', [i18nName]);
    }

    /**
     * 电源选择个数错误
     */
    checkSelectedNum(num: number): string {
        return this.i18n.get('system.hardware_term_planePrimaryPower_selectPSU_valid', [num]);
    }

    /**
     * 长度范围错误
     * @param min
     * @param max
     */
    rangeByteLengthErr(min: number, max: number): string {
        return this.i18n.get('common.length_msg', [min, max]);
    }

    /**
     * 名称格式错误
     * @param name
     */
    commonNameErr(name: string): string {
        const i18nName = this.i18nLabel[name];
        return this.i18n.get('common.nameformat_validation', [i18nName]);
    }

    repeatNameErr(): string {
        return this.i18n.get('common.allCanNotEquall_msg', [this.get('common.name_column')]);
    }

    nameErrMsg(name: string): string {
        const i18nName = this.i18nLabel[name];
        return this.i18n.get('common.nameformat_validation', [i18nName]);
    }

    lengthErrMsg(min: number, max: number): string {
        return this.i18n.get('common.length_msg', [min, max]);
    }

    eraseDiskName(location: string): string {
        return this.get('rest.erasingData_msg', [location]);
    }

    repeatErrMag(name: string): string {
        const i18nName = this.i18nLabel[name];
        return this.i18n.get('common.allCanNotEquall_msg', [i18nName]);
    }

    get(key: string, params?: any[] | boolean, colon?: boolean): string {
        return this.i18n.get(key, params, colon);
    }

    getI18n(kv: { [key: string]: string }): { [key: string]: string } {
        const keys = Object.keys(kv);
        const result = {};
        keys.forEach((key) => {
            result[key] = this.i18n.get(kv[key]);
        });
        return result;
    }

    obj2Options(obj: { [key: string]: string }): SelectOption[] {
        return Object.keys(obj).map((key) => {
            const option = {
                value: key,
                label: obj[key],
            };
            return option;
        });
    }

    /**
     * 操作名
     * @param status true 开启  false 关闭
     * @param name 端口名
     */
    switchName(status: boolean, name: string): string {
        const str = status ? this.i18nLabel.enablegSNSDTitle : this.i18nLabel.disablegSNSDTitle;
        return this.i18n.get(str, [name]);
    }

    /**
     * 生成接口模块名称列表
     */
    getIntfModuleModel(): { [key: string]: string } {
        const list = {};
        Object.keys(intfModuleList).forEach((key) => {
            list[key] = this.get(intfModuleList[key].i18nKey);
        });
        return list;
    }

    /**
     * 生成电源平面label
     */
    powerIndexLabel(index: string): string {
        return this.get('system.hardware_term_planePrimaryPower_lab', [index]);
    }
}


